import React, { useState, useEffect } from "react";
import axiosInstance from "../../../services/axiosInstance";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
    TextField,
    Button,
    MenuItem,
    Box,
    IconButton,
    InputAdornment,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Typography,
    Divider,
    Chip,
    Fade,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
    Close,
    Person,
    AdminPanelSettings,
    VisibilityOutlined,
    Email,
    Badge,
    Lock,
} from "@mui/icons-material";
import apiEndPointsConfig from "../../../services/apiEndPoints";

/* -------------------- Validation Schema -------------------- */
const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required("Full name is required")
        .min(2, "Name is too short")
        .max(50, "Name is too long")
        .trim(),
    email: Yup.string()
        .email("Invalid email address")
        .required("Email is required")
        .lowercase()
        .trim(),
    password: Yup.string().when("isEdit", {
        is: false,
        then: () => Yup.string().required("Password is required").min(8, "Minimum 8 characters"),
        otherwise: () => Yup.string().min(8, "Minimum 8 characters").optional(),
    }),
    role: Yup.string()
        .required("Role is required")
        .oneOf(["member", "admin", "viewer", "account manager"]),
});

/* -------------------- Component -------------------- */
const TeamMemberModal = ({ open, onClose, clientId, onSuccess, initialValues, isEdit = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const defaultValues = {
        name: "",
        email: "",
        password: "",
        role: "member",
        isEdit: isEdit,
    };

    const formValues = initialValues ? { ...initialValues, isEdit } : defaultValues;

    const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
        setMessage({ type: "", text: "" });

        try {

            let response;
            if (isEdit) {
                // Prepare update data: don't send empty password
                const updateData = { ...values };
                if (!updateData.password) delete updateData.password;

                response = await axiosInstance.put(
                    `${apiEndPointsConfig.updateTeamMember}/${values.id}`,
                    updateData
                );
            } else {
                response = await axiosInstance.post(
                    apiEndPointsConfig.addTeamMember,
                    { ...values, clientId }
                );
            }

            if (response.data.success) {
                setMessage({
                    type: "success",
                    text: response.data.message || `Member ${isEdit ? "updated" : "added"} successfully!`,
                });

                if (!isEdit) resetForm();

                setTimeout(() => {
                    onSuccess();
                    onClose();
                    setMessage({ type: "", text: "" });
                }, 1500);
            }
        } catch (error) {
            if (error.response?.data?.field) {
                setFieldError(error.response.data.field, error.response.data.message);
            }
            setMessage({
                type: "error",
                text: error.response?.data?.message || `Failed to ${isEdit ? "update" : "add"} team member.`,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(e, reason) => reason !== "backdropClick" && onClose()}
            maxWidth="xs"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 400 }}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                },
            }}
        >
            {/* ---------------- Header ---------------- */}
            <DialogTitle sx={{ p: 3, pb: 2, background: "linear-gradient(to right, #f8faff, #ffffff)" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" fontWeight={800} sx={{ color: "#1a237e", letterSpacing: "-0.5px" }}>
                            {isEdit ? "Edit Member" : "New Team Member"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isEdit ? "Update account details and permissions" : "Invite a new member to your workspace"}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "text.secondary", "&:hover": { color: "#d32f2f", bgcolor: "#fff1f0" } }}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            {/* ---------------- Content ---------------- */}
            <DialogContent sx={{ p: 3, pt: 4 }}>
                {message.text && (
                    <Fade in={!!message.text}>
                        <Alert
                            severity={message.type}
                            sx={{ mb: 3, borderRadius: 2 }}
                            onClose={() => setMessage({ type: "", text: "" })}
                        >
                            {message.text}
                        </Alert>
                    </Fade>
                )}

                <Formik
                    initialValues={formValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ errors, touched, isSubmitting, isValid }) => (
                        <Form>
                            <Box display="flex" flexDirection="column" gap={3}>
                                {/* Name */}
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="name"
                                    label="Full Name"
                                    placeholder="e.g. John Doe"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Badge sx={{ color: touched.name ? "#4a90e2" : "action.active" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Email */}
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="email"
                                    label="Email Address"
                                    placeholder="john@example.com"
                                    type="email"
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: touched.email ? "#4a90e2" : "action.active" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Password */}
                                <Field
                                    as={TextField}
                                    fullWidth
                                    name="password"
                                    label={isEdit ? "Update Password (Leave blank to keep current)" : "Password"}
                                    type={showPassword ? "text" : "password"}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: touched.password ? "#4a90e2" : "action.active" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Role Selection */}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: "text.primary" }}>
                                        Select Permissions
                                    </Typography>
                                    <Field
                                        as={TextField}
                                        select
                                        fullWidth
                                        name="role"
                                        error={touched.role && Boolean(errors.role)}
                                        helperText={touched.role && errors.role}
                                    >
                                        <MenuItem value="member">
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Person fontSize="small" sx={{ color: "#4caf50" }} />
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>Member</Typography>
                                                    <Typography variant="caption" color="text.secondary">Default access to tickets & leads</Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="admin">
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <AdminPanelSettings fontSize="small" sx={{ color: "#ff9800" }} />
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>Admin</Typography>
                                                    <Typography variant="caption" color="text.secondary">Full control over team & settings</Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="viewer">
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <VisibilityOutlined fontSize="small" sx={{ color: "#2196f3" }} />
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>Viewer</Typography>
                                                    <Typography variant="caption" color="text.secondary">Read-only access to reports & data</Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="account manager">
                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <Person fontSize="small" sx={{ color: "#9c27b0" }} />
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>Account Manager</Typography>
                                                    <Typography variant="caption" color="text.secondary">Manage assigned leads & deals</Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    </Field>
                                </Box>

                                {/* Actions */}
                                <DialogActions sx={{ px: 0, pt: 2, justifyContent: "space-between" }}>
                                    <Button onClick={onClose} variant="text" color="inherit" sx={{ px: 3 }}>
                                        Cancel
                                    </Button>

                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        variant="contained"
                                        sx={{
                                            minWidth: 150,
                                            height: 48,
                                            borderRadius: 2.5,
                                            fontWeight: 700,
                                            textTransform: "none",
                                            fontSize: "1rem",
                                            boxShadow: "0 8px 20px rgba(53, 122, 189, 0.3)",
                                            background: "linear-gradient(135deg, #4a90e2, #357abd)",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #357abd, #2c6cb3)",
                                                boxShadow: "0 10px 25px rgba(53, 122, 189, 0.4)",
                                            },
                                            "&:disabled": {
                                                bgcolor: "action.disabledBackground",
                                            },
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={24} sx={{ color: "#fff" }} />
                                        ) : isEdit ? (
                                            "Save Changes"
                                        ) : (
                                            "Create Member"
                                        )}
                                    </Button>
                                </DialogActions>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default TeamMemberModal;
